<template>
    <div>
        <h3>{{slide.slide.title}}</h3>
        <div v-if="slide.slide.corpus">
            <div style="font-size: 125%;" v-html="sanitize(slide.slide.corpus)"></div>
            <hr>
        </div>
        <ResourceGallery :resourceList="slide.slide.resources"></ResourceGallery>
        <div v-show="!complete">
            <div style="margin: auto; display: inline-block; padding: 20px;">
                <h3 style="margin-bottom: 10px">Instructions:</h3>
                <span style="font-size: 110%; text-align: justify; float: left;">
                    - Drag each diagnosis to the category in which it belongs using the handle (horizontal lines) in the top left.
                    <span v-if="!slide.freeResponse">
                        <br>
                        - Your score is calculated based on whether you have correctly categorized each diagnosis by likelihood.
                        <br>
                        - Correctly place the top diagnosis first under "High Likelihood" for {{topDxWorth}} extra points! Sorting is otherwise optional.
                    </span>
                    <br>
                    - It is a good idea (but not required) to write an explanation for how you categorized each diagnosis.
                    <br>
                    - Some diagnosis may not be included, feel free to add them to the differential yourself.
                </span>
            </div>
            <br>
            <hr>
        </div>
        <div v-if="complete">
            <h2 v-if="!slide.freeResponse" style="font-weight: normal">
                Your score is: {{score}}%
            </h2>
            <h2>
                {{showExpertAnswer ? 'Expert' : 'Your'}} Differential:
            </h2>
            <hr>
        </div>
        <div v-if="slideChangedSinceAttempt" class="question-review-change-notice">
            <b>NOTICE: Section has changed since this attempt!</b>
            <br><br>
        </div>
        <md-button class="md-raised md-primary" @click="vertDx=!vertDx" style="transform: scale(.92)">
            Switch to {{vertDx ? 'Horizontal' : 'Vertical'}} mode
        </md-button>
        <hr>
        <div :class="{'ddx-editor': true, 'ddx-editor-vert': vertDx}">
            <div class="ddx-col"
            v-for="priorityElem in priorityGroups" 
            :style="{'background-color': lookupPriorityBgColor(priorityElem)}"
            :key="priorityElem">
                <h3>{{showExpertAnswer ? 'Expert - ' : ''}}{{priorityElem}}</h3>
                <hr>
                <draggable 
                v-model="dxGroups[priorityElem]" 
                class="dragArea" 
                @end="checkMove(false); checkMove(true);"
                :options="{group:'ddx', handle :'.drag-handle'}">
                    <transition-group name="ddx-entry-move">
                        <div 
                        class="ddx-entry md-elevation-6"
                        v-for="(dxOption, dxIndex) in dxGroups[priorityElem]" 
                        :style="{'background-color': lookupPriorityColor(priorityElem, dxOption)}"
                        :key="showExpertAnswer + '-' + dxOption.id">
                            <span>
                                <md-icon v-show="!complete" class="drag-handle">drag_handler</md-icon>
                            </span>
                            <div :style="{'min-width': !dxOption.userCreated || complete ? '90%' : '80%', 'margin-left': '-10px'}"> 
                                <div v-show="dxOption.order == 1 && priorityElem == 'High Likelihood'" style="font-weight: bold; margin-bottom: 6px">
                                    TOP DIAGNOSIS
                                </div>
                                <div class="dx-order-input" v-show="!complete">
                                    <md-field class="order-input">
                                        <span class="md-prefix">#</span>
                                        <md-input v-model="dxOption.order" type="number" min=1 :max="dxGroups[priorityElem].length"
                                        @change="rearrangeDiagnosis(priorityElem, dxIndex)" style="text-align: center"/>
                                    </md-field>
                                    <select v-model="dxOption.priority" @change="checkMove(true)" class="priority-select"
                                    :style="{'background-color': lookupPriorityBgColor(priorityElem, dxOption)}">
                                        <option 
                                        v-for="priorityElem in priorityGroups" 
                                        :value="priorityElem"
                                        :key="priorityElem">
                                            {{priorityElem}}
                                        </option>
                                    </select>
                                </div>
                                <div class="dx-entry-input">
                                    <md-field>
                                        <editable
                                            :content="dxOption.dx"
                                            @update="dxOption.dx = $event" 
                                            class="div-textarea"
                                            :readonly="complete || !dxOption.userCreated"
                                            style="width: 100%" 
                                            :placeholder="(showExpertAnswer ? 'Expert ' : '') + 'Diagnosis #' + (dxIndex+1)">
                                        </editable>
                                    </md-field>
                                    <md-field>
                                        <editable 
                                            :content="dxOption.explanation"
                                            @update="dxOption.explanation = $event"
                                            class="div-textarea"
                                            :readonly="complete" 
                                            style="width: 100%" 
                                            :placeholder="(showExpertAnswer ? 'Expert ' : '') + 'Explanation #' + (dxIndex+1)">
                                        </editable>
                                    </md-field>
                                </div>
                            </div>
                            <md-button 
                                @click="deleteDDx(priorityElem, dxIndex)"
                                v-show="dxOption.userCreated && !complete"
                                class="md-icon-button delete-btn">
                                <md-icon class="md-accent">
                                    cancel
                                </md-icon>
                            </md-button>
                            <md-tooltip v-if="complete && !showExpertAnswer" md-direction="right">
                                Expert categorized this as: {{lookupDiagnosisAnswer(dxOption).priority}}
                            </md-tooltip>
                        </div>
                    </transition-group>
                    <div slot="footer">
                        <md-button v-show="!complete" @click="addDDxOpt(priorityElem)" class="md-raised md-primary">
                            Add Diagnosis
                        </md-button>
                    </div>
                </draggable>
            </div>
        </div>
        <div v-if="complete">
            <md-button 
            class="md-primary md-raised"
            @click="showExpertAnswer=!showExpertAnswer; sortDiagnosisData()">
                <span v-if="showExpertAnswer">
                    Show Your Response
                </span>
                <span v-else>
                    Show Expert Response
                </span>
            </md-button>
        </div>
        <div v-if="complete && slide.answer"> 
            <hr> 
            <h2>Explanation</h2> 
            <hr> 
            <span v-html="sanitize(slide.answer)"></span> 
        </div>
        <CitationViewer :citations="slide.slide.citations" v-if="complete"/>
        <ResourceGallery :resourceList="slide.a_resources" v-if="complete"></ResourceGallery>

        <hr>
        <h3 v-if="!complete && dxGroups['High Likelihood'] && dxGroups['High Likelihood'][0]">
            Top Diagnosis: {{dxGroups["High Likelihood"][0].dx}}
        </h3>
        <br>
        <h3 v-if="complete && dxGroups['High Likelihood'] && dxGroups['High Likelihood'][0]">
            <span v-if="showExpertAnswer">Expert's</span><span v-else>Your</span> Top Diagnosis: {{dxGroups["High Likelihood"][0].dx}}
        </h3>
        <md-button class="md-raised md-primary" v-if="!complete" @click="submitAnswer();">
            Submit
        </md-button>
        <md-button class="md-raised md-primary" v-if="complete" @click="$emit('setDone', [slide.slide.order, true])">
            Continue
        </md-button>
    </div>
</template>

<script>
function shuffle(a) {
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

import draggable from 'vuedraggable'
import ResourceGallery from './Gallery.vue'
import CitationViewer from './CitationViewer.vue'
import editable from '../Editable.vue'
import hasher from "object-hash"

const ObjectID = require("bson-objectid");

export default {
    props: {
        slide: {
            type: Object
        },
        attemptRecord: {
            type: Object
        },
        complete: {
            type: Boolean
        },
        sanitize: {
            type: Function
        },
    },
    data: function(){
        return {
            vertDx: true,
            answer: [],
            slideChangedSinceAttempt: false,
            expertAnswer: [],
            priorityGroups: ["Not Likely", "Low Likelihood", "High Likelihood"],
            dxGroups: {},
            showExpertAnswer: false,
            topDxWorth: 2,
            score: 0,
        }
    },
    components: {
        editable,
        draggable,
        ResourceGallery,
        CitationViewer,
    },
    methods: {
        lookupDiagnosisAnswer: function(dxOption){
            let answer = this.expertAnswer.find((ddxElem) => {
                return ddxElem.dx == dxOption.dx
            })
            return answer ? answer : {priority: "Unknown"}
        },
        lookupDiagnosisCorrect: function(priority, dxOption){
            // Run through the expert's diagnosis list
            return this.expertAnswer.some((ddxElem) => {
                // And check if there is a diagnosis which matches by name and priority (and is thus correctly categorized)
                return (ddxElem.dx.toLowerCase() == dxOption.dx.toLowerCase()) && (ddxElem.priority == priority)
            })
        },
        lookupPriorityColor: function(priority, dxOption){
            if (priority == "High Likelihood" && dxOption.order == 1) {
                // Highlight top diagnosis w/ red color
                return "#f77890"
            }
            if(this.complete && dxOption.userCreated){
                // User created diagnosis have a white background
                return "white"
            }
            if(this.complete && !this.showExpertAnswer){
                // Color coding based on correct categorization
                return this.lookupDiagnosisCorrect(priority, dxOption) ? "#74e38d" : "#fd8585"
            }
            else {
                // Color based on category
                const colorLookup = {
                    "Not Likely": "#eee", 
                    "Low Likelihood": "#fdff9a", 
                    "High Likelihood": "#ffbe93"
                    //Green = 74e38d
                }
                if(!Object.keys(colorLookup).includes(priority)){
                    return "white"
                }
                return colorLookup[priority]
            }
        },
        lookupPriorityBgColor: function(priority, dxOption){
            if(dxOption && dxOption.order != undefined) {
                if (priority == "High Likelihood" && dxOption.order == 1) {
                    return "#dc6b81"
                }
            }
            const colorLookup = {
                "Not Likely": "#ddd", 
                "Low Likelihood": "#f8fa63", 
                "High Likelihood": "#ffa56a"
                //Green = 4ace68
            }
            if(!Object.keys(colorLookup).includes(priority)){
                return "white"
            }
            return colorLookup[priority]
        },
        submitAnswer: function(){
            this.answer = JSON.parse(JSON.stringify(this.unsortDiagnosisData()))
            this.slide.complete = true
            var performance = {
                performance: {
                    "Diagnosis": this.dxGroups,
                    "QHash": hasher(JSON.stringify(this.slide))
                },
            }
            this.score = this.scoreAnswer();
            if(!this.slide.freeResponse && this.score != undefined){
                performance["score"] = this.score;
            }
            this.showExpertAnswer=true; 
            this.sortDiagnosisData();
            this.$emit('setDone', [this.slide.slide.order, false, performance])
        },
        scoreAnswer: function() {
            if(this.slide.freeResponse){
                return undefined
            }
            // Set value of correctly identifying the top dx to be worth 
            // the same as correctly categorizing n = 2 other diagnosis
            let totalDDx = 0 + this.topDxWorth
            let correctDDx = 0
            if (this.answer[0].id == this.expertAnswer[0].id) {
                // Learner has correctly identified the top dx
                correctDDx += this.topDxWorth;
            }
            // Iterate through each of the learner's diagnosis
            this.answer.forEach((answerElem) => {
                // Ignore learner created diagnosis, we can't score these
                if(!answerElem.userCreated){
                    // Check if the learner correctly categorized the diagnosis
                    if(this.lookupDiagnosisCorrect(answerElem.priority, answerElem)) correctDDx++;
                    totalDDx++
                }
            })
            return parseFloat((100*correctDDx/totalDDx).toFixed(1))
        },
        createDDxEntry: function(priority){
            return {
                id: ObjectID(),
                order: this.dxGroups[priority].length+1,
                dx: "",
                priority,
                explanation: "",
                userCreated: true,
            }
        },
        deleteDDx: function(priorityElem, ddxIndex){
            this.dxGroups[priorityElem].splice(ddxIndex, 1)
        },
        addDDxOpt: function(priority) {
            if(!this.dxGroups[priority]) this.dxGroups[priority] = []
            this.dxGroups[priority].push(this.createDDxEntry(priority))
        },
        rearrangeDiagnosis(priority, oldIndex){
            let rearrange = () => {
                let newIndex = this.dxGroups[priority][oldIndex].order-1
                let newDxGroup = []
                if(newIndex < 0){
                    newIndex = 0 
                } 
                if(newIndex > this.dxGroups[priority].length-1){
                    newIndex = this.dxGroups[priority].length-1
                }
                if(newIndex > oldIndex){
                    this.dxGroups[priority].forEach((ddxElem, ddxIndex) => {
                        if(ddxIndex > oldIndex && ddxIndex <= newIndex){
                            ddxElem.order = ddxIndex
                            newDxGroup[ddxIndex-1] = ddxElem
                        } else if(oldIndex == ddxIndex) {
                            ddxElem.order = newIndex + 1
                            newDxGroup[newIndex] = ddxElem
                        } else {
                            ddxElem.order = ddxIndex + 1
                            newDxGroup[ddxIndex] = ddxElem
                        }
                    })
                } else if (newIndex < oldIndex){
                    this.dxGroups[priority].forEach((ddxElem, ddxIndex) => {
                        if(ddxIndex < oldIndex && ddxIndex >= newIndex){
                            ddxElem.order = ddxIndex+2
                            newDxGroup[ddxIndex+1] = ddxElem
                        } else if(oldIndex == ddxIndex) {
                            ddxElem.order = newIndex + 1
                            newDxGroup[newIndex] = ddxElem
                        } else {
                            ddxElem.order = ddxIndex + 1
                            newDxGroup[ddxIndex] = ddxElem
                        }
                    })
                } else {
                    newDxGroup = this.dxGroups[priority]
                }
                this.$set(this.dxGroups, priority, newDxGroup)
                this.unsortDiagnosisData()
                this.sortDiagnosisData()
            }
            setTimeout(rearrange, 250)
        },
        sortDiagnosisData: function(){
            let renderData = {}
            this.priorityGroups.forEach((priorityElem) => renderData[priorityElem] = [])
            let dxOptions
            if(this.showExpertAnswer){
                dxOptions = this.expertAnswer
            } else {
                if(this.complete){
                    dxOptions = this.answer
                }
                else {
                    dxOptions = this.unsortDiagnosisData(true)
                }
            }
            dxOptions.forEach((ddxOpt) => {
                if(!renderData[ddxOpt.priority]) renderData[ddxOpt.priority] = []
                ddxOpt.order = renderData[ddxOpt.priority].length + 1                    
                renderData[ddxOpt.priority].push(ddxOpt)
            })
            this.dxGroups = renderData
        },
        setupDiagnosisData: function(){
            let renderData = {}
            this.priorityGroups.forEach((priorityElem) => renderData[priorityElem] = [])
            if (!this.slide.freeResponse) {
                let inputData = JSON.parse(JSON.stringify(this.slide.ddx_opts))
                if(!inputData) inputData = []
                shuffle(inputData)
                inputData.forEach((ddxOpt) => {
                    if(!renderData[ddxOpt.priority]) renderData[ddxOpt.priority] = []
                    ddxOpt.explanation = ""
                    ddxOpt.priority = "Not Likely"
                    ddxOpt.order = renderData[ddxOpt.priority].length + 1                    
                    renderData[ddxOpt.priority].push(ddxOpt)
                })
            }
            this.dxGroups = renderData
        },
        unsortDiagnosisData: function(usePriority){
            let dbData = []
            Array().concat(this.priorityGroups).reverse().forEach((priorityElem) => {
                if(this.dxGroups[priorityElem]){
                    this.dxGroups[priorityElem].forEach((ddxElem) => {
                        if(!ddxElem.priority || !usePriority) ddxElem.priority = priorityElem
                        dbData.push(ddxElem)
                    })
                }
            })
            dbData = dbData.filter((ddxElem) => {
                return ddxElem.dx && ddxElem.dx != ""
            })
            return dbData
        },
        checkMove: function(usePriority){
            this.unsortDiagnosisData(usePriority)
            if(usePriority) this.sortDiagnosisData()
        },
        loadAttemptAnswer: function(){
            if(this.attemptRecord && this.attemptRecord.performance && Object.keys(this.attemptRecord.performance).length != 0 && this.attemptRecord.performance["Diagnosis"]){
                this.dxGroups = this.attemptRecord.performance["Diagnosis"]
                this.answer = JSON.parse(JSON.stringify(this.unsortDiagnosisData()))
                this.slideChangedSinceAttempt = hasher(JSON.stringify(this.slide)) != this.attemptRecord.performance["QHash"]
            }
            else {
                this.setupDiagnosisData()
            }
        }
    },
    watch: {
        "attemptRecord": function() {
            this.loadAttemptAnswer()
        }
    },
    mounted: function(){
        this.$nextTick(function() {
            this.expertAnswer = JSON.parse(JSON.stringify(this.slide.ddx_opts))
            this.loadAttemptAnswer()
        })
    }
}
</script>

<style scoped>
    h3{
        margin: 5px;
    }
    .md-field{
        text-align: left;
        width: 90% !important;
        margin: auto;
        margin-bottom: 10px;
    }
    .ddx-editor{
        transition: all 600ms;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
    }
    .ddx-editor.ddx-editor-vert{
        flex-direction: row !important;
        width: 100%;
        overflow-x: auto;
        justify-content: flex-start;
        padding-bottom: 15px;
    }
    .vertical-layout .ddx-editor{
        margin: 0 -20px;
        width: calc(100% + 40px);
    }
    .ddx-col{
        border: 1px solid black;
        border-radius: 8px;
        min-width: 90%;
        margin: 10px 0;
        padding: 10px 0px;
    }
    .ddx-editor-vert .ddx-col{
        width: 31%;  
        height: 84vh;  
        padding: 10px 5px;  
        min-width: 340px;  
        margin: 8px 8px;
    }
    .vertical-layout .ddx-col {
        font-size: 90%;
        min-width: 94%;
    }
    .priority-select{
        height: 36px;
        text-align-last: center;
    }
    .vertical-layout .priority-select{
        font-size: 100%;
    }
    .order-input{
        max-width: 50px; 
        margin: 0px; 
        margin-right: 5px;
        margin-top: -12px;
        font-size: 90%;
    }
    .order-input input[type=number] {
        padding-right: 10px;
    }
    .order-input input[type=number]::-webkit-inner-spin-button, 
    .order-input input[type=number]::-webkit-outer-spin-button { 
        -webkit-appearance: none;
    }
    .ddx-editor-vert .order-input{
        margin-right: 20px; 
    }
    .vertical-layout .order-input{
        margin-right: 10px; 
    }
    .order-input input{
        max-width: 80%;
    }
    .vertical-layout .order-input input, .vertical-layout .order-input .md-prefix{
        font-weight: bold;
        font-size: 100%;
    }
    .ddx-col .draggable{
        min-height: 120px;
    }
    .ddx-entry{
        transition: all 600ms;
        margin: 0 15px 25px 15px;
        padding: 8px;
        border-radius: 5px;
        display: flex;
        min-width: 240px;
        position: relative;
    }
    .dx-entry-input{
        max-width: 300px;
    }
    .ddx-editor-vert .ddx-entry{
        width: 92%; 
        margin: 12px auto; 
    }
    .ddx-entry-move-enter, .ddx-entry-move-leave-active {
        opacity: 0;
    }
    .dragArea{
        display: flex;
        flex-direction: column;
        width: 100%;
    }
    .ddx-editor-vert .dragArea{
        height: 86%;  
    }
    .dragArea > span{
        overflow-x: scroll;
        display: flex;
        min-height: 120px;
    }
    .ddx-editor-vert .dragArea > span{
        overflow-x: hidden;  
        overflow-y: scroll;  
        display: block;
        min-height: 86%; 
    }
    .drag-handle{
        cursor: move;
        padding: 1px 0 20px 0;
    }
    .vertical-layout .drag-handle {
        display: none;
    }
    .md-textarea{
        overflow-y: hidden !important;
        margin: 4px;
    }
    [contenteditable=true]:empty:before {
        content: attr(placeholder);
        color: #868686;
        display: block; /* For Firefox */
    }
    .div-textarea{
        cursor: text;
        line-height: 1.15;  
        padding: 5px 7px;
        max-height: 100px; 
        overflow-y: auto; 
    }
    .vertical-layout .free-response-switch{
        margin-bottom: 34px;
    }
    .dx-order-input {
        display: flex; 
        align-items: center; 
        justify-content: center;
    }
    .delete-btn{
        position: absolute;
        top: 5px;
        right: 0px;
    }
</style>